import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Container, Row, Col, Input, Spinner, Alert } from "reactstrap";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  API_URL_chatbot_access,
  API_URL_chatbot_admin_conversations,
  API_URL_chatbot_admin_tool_gaps,
} from "../constants";
import { getAuthHeaders } from "../utils/auth";

const AIAdmin = () => {
  const [accessChecked, setAccessChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [convsLoading, setConvsLoading] = useState(false);
  const [convsError, setConvsError] = useState("");

  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [tab, setTab] = useState("conversations"); // "conversations" | "gaps"
  const [gaps, setGaps] = useState([]);
  const [gapsLoading, setGapsLoading] = useState(false);

  useEffect(() => {
    axios
      .get(API_URL_chatbot_access, { headers: getAuthHeaders() })
      .then((res) => setIsAdmin(!!res.data?.is_admin))
      .catch(() => setIsAdmin(false))
      .finally(() => setAccessChecked(true));
  }, []);

  const loadList = useCallback(() => {
    setConvsLoading(true);
    setConvsError("");
    axios
      .get(API_URL_chatbot_admin_conversations, { headers: getAuthHeaders() })
      .then((res) => setConversations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        setConvsError("No se pudo cargar el log.");
      })
      .finally(() => setConvsLoading(false));
  }, []);

  useEffect(() => {
    if (isAdmin) loadList();
  }, [isAdmin, loadList]);

  const loadGaps = useCallback(() => {
    setGapsLoading(true);
    axios
      .get(API_URL_chatbot_admin_tool_gaps, { headers: getAuthHeaders() })
      .then((res) => setGaps(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setGapsLoading(false));
  }, []);

  useEffect(() => {
    if (isAdmin && tab === "gaps") loadGaps();
  }, [isAdmin, tab, loadGaps]);

  const toggleGapResolved = async (id, current) => {
    try {
      await axios.patch(
        `${API_URL_chatbot_admin_tool_gaps}${id}/`,
        { resolved: !current },
        { headers: getAuthHeaders() }
      );
      loadGaps();
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = useMemo(() => {
    if (!filter.trim()) return conversations;
    const q = filter.toLowerCase();
    return conversations.filter(
      (c) =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.username || "").toLowerCase().includes(q)
    );
  }, [conversations, filter]);

  const openConversation = (c) => {
    setSelected({ ...c, messages: [] });
    setDetailLoading(true);
    axios
      .get(`${API_URL_chatbot_admin_conversations}${c.id}/`, { headers: getAuthHeaders() })
      .then((res) => setSelected(res.data))
      .catch(() => setSelected({ ...c, _error: "No se pudo cargar." }))
      .finally(() => setDetailLoading(false));
  };

  if (!accessChecked) {
    return (
      <Container className="text-center my-5">
        <Spinner color="primary" />
      </Container>
    );
  }
  if (!isAdmin) return <Navigate to="/home" replace />;

  return (
    <Container fluid style={{ marginTop: 20, maxWidth: 1500, paddingLeft: 24, paddingRight: 24 }}>
      <Row>
        <Col>
          <h1 style={{ marginBottom: 4 }}>Log del Asistente AI</h1>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            Revisa todas las preguntas hechas al asistente. Vista de solo lectura.
          </p>
        </Col>
      </Row>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid #e5e7eb" }}>
        {[
          { key: "conversations", label: "Conversaciones" },
          { key: "gaps", label: "Herramientas faltantes" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 16px",
              border: "none",
              background: "transparent",
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? "#dc3545" : "#6b7280",
              borderBottom: tab === t.key ? "2px solid #dc3545" : "2px solid transparent",
              cursor: "pointer",
              fontSize: "0.9375rem",
            }}
          >
            {t.label}
            {t.key === "gaps" && gaps.filter((g) => !g.resolved).length > 0 && (
              <span style={{
                marginLeft: 6,
                padding: "1px 7px",
                backgroundColor: "#dc3545",
                color: "#fff",
                borderRadius: 10,
                fontSize: "0.75rem",
              }}>
                {gaps.filter((g) => !g.resolved).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "conversations" && (
        <Row className="mb-3">
          <Col md={6}>
            <Input
              type="text"
              placeholder="Buscar por usuario o título..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </Col>
          <Col md={6} className="text-md-end">
            <span className="text-muted">
              Mostrando {filtered.length} de {conversations.length} conversaciones
            </span>
          </Col>
        </Row>
      )}

      {convsError && <Alert color="danger">{convsError}</Alert>}

      {tab === "gaps" && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {gapsLoading ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spinner size="sm" color="secondary" />
            </div>
          ) : gaps.length === 0 ? (
            <div style={{ padding: 18, color: "#9ca3af", fontStyle: "italic" }}>
              No hay huecos de herramientas registrados. Cuando el asistente diga "no tengo herramienta para X", aparecerá aquí.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead style={{ background: "#f9fafb" }}>
                <tr>
                  <th style={{ padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Herramienta sugerida</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Descripción</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Pregunta original</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Usuario</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Fecha</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>Resuelto</th>
                </tr>
              </thead>
              <tbody>
                {gaps.map((g) => (
                  <tr
                    key={g.id}
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      opacity: g.resolved ? 0.5 : 1,
                      textDecoration: g.resolved ? "line-through" : "none",
                    }}
                  >
                    <td style={{ padding: "10px 12px", fontWeight: 600, fontFamily: "monospace" }}>
                      {g.suggested_name || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>{g.description}</td>
                    <td style={{ padding: "10px 12px", color: "#6b7280", fontStyle: "italic" }}>
                      {g.user_question?.slice(0, 80)}{g.user_question?.length > 80 ? "…" : ""}
                    </td>
                    <td style={{ padding: "10px 12px" }}>{g.username || "—"}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#9ca3af" }}>
                      {new Date(g.created_at).toLocaleString("es", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={g.resolved}
                        onChange={() => toggleGapResolved(g.id, g.resolved)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "conversations" && (
      <div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
        {/* List */}
        <div
          style={{
            width: 360,
            flexShrink: 0,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            maxHeight: "70vh",
          }}
        >
          <div style={{ overflowY: "auto", flex: 1 }}>
            {convsLoading ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Spinner size="sm" color="secondary" />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 14, color: "#9ca3af", fontSize: "0.875rem", fontStyle: "italic" }}>
                No hay conversaciones.
              </div>
            ) : (
              filtered.map((c) => {
                const active = selected?.id === c.id;
                return (
                  <div
                    key={c.id}
                    onClick={() => openConversation(c)}
                    style={{
                      padding: "10px 12px",
                      borderBottom: "1px solid #f3f4f6",
                      cursor: "pointer",
                      backgroundColor: active ? "#fef2f2" : "transparent",
                      borderLeft: active ? "3px solid #dc3545" : "3px solid transparent",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: "#dc3545",
                        marginBottom: 2,
                      }}
                      title={c.username}
                    >
                      {c.username}
                    </div>
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={c.title || "(sin título)"}
                    >
                      {c.title || "(sin título)"}
                    </div>
                    <div style={{ fontSize: "0.6875rem", color: "#9ca3af", marginTop: 2 }}>
                      {new Date(c.updated_at).toLocaleString("es", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      · {c.message_count} mensajes
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Detail */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fafafa",
            padding: 16,
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          {!selected ? (
            <div className="text-muted" style={{ fontStyle: "italic" }}>
              Selecciona una conversación a la izquierda para ver el detalle.
            </div>
          ) : detailLoading ? (
            <div className="text-center my-3">
              <Spinner size="sm" color="primary" />
            </div>
          ) : selected._error ? (
            <Alert color="danger">{selected._error}</Alert>
          ) : (
            <>
              <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#1f2937" }}>
                  {selected.title || "(sin título)"}
                </div>
                <div style={{ fontSize: "0.8125rem", color: "#6b7280", marginTop: 2 }}>
                  Usuario: <strong>{selected.username}</strong>
                  {" · "}
                  Creada: {new Date(selected.created_at).toLocaleString("es")}
                </div>
              </div>

              {(selected.messages || []).map((m, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: m.role === "user" ? "#1f2937" : "#dc3545",
                      marginBottom: 4,
                    }}
                  >
                    {m.role === "user" ? selected.username : "Asistente"}
                    <span style={{ marginLeft: 6, color: "#9ca3af", fontWeight: 400 }}>
                      · {new Date(m.created_at).toLocaleString("es", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <div style={{ lineHeight: 1.5 }}>
                    {m.role === "assistant" ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {m.content}
                      </ReactMarkdown>
                    ) : (
                      <div style={{ whiteSpace: "pre-wrap" }}>{m.content}</div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      )}
    </Container>
  );
};

export default AIAdmin;
